/**
 * Roles and back-office authorisation.
 *
 * Edge-safe on purpose: `src/proxy.ts` imports this, so nothing here may pull
 * in Prisma, bcrypt or any Node-only builtin. It is the single source of truth
 * for "who may open which part of /admin" — the proxy, the admin layout, the
 * sidebar and every /api/admin route all read the same table below, so a new
 * restricted page can never be gated in one place and forgotten in another.
 */

export const ROLES = ['ADMIN', 'MEDEWERKER', 'CUSTOMER'] as const
export type Role = (typeof ROLES)[number]

/** Roles that may open the back office at all. A CUSTOMER never can. */
export const STAFF_ROLES: readonly Role[] = ['ADMIN', 'MEDEWERKER']

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN:      'Beheerder',
  MEDEWERKER: 'Medewerker',
  CUSTOMER:   'Klant',
}

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  ADMIN:      'Mag alles in de back-office, inclusief Beheer (gebruikers, logboek en instellingen).',
  MEDEWERKER: 'Mag alles in de back-office behalve Beheer, dus geen gebruikers, logboek of instellingen.',
  CUSTOMER:   'Gewone webshopklant. Heeft géén toegang tot de back-office.',
}

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

/** True for a back-office account (ADMIN or MEDEWERKER). */
export function isStaff(role?: string | null): boolean {
  return role === 'ADMIN' || role === 'MEDEWERKER'
}

/** True only for a full administrator. */
export function isAdminRole(role?: string | null): boolean {
  return role === 'ADMIN'
}

/**
 * The "Beheer" section: administrator-only. Paths are matched as prefixes, so
 * `/admin/gebruikers/abc` and `/api/admin/users/abc/password` are covered by
 * their parent entry.
 *
 * Note what is deliberately NOT here: `/api/admin/planning/settings` belongs to
 * Openingstijden, which lives under Overzicht and is open to every staff
 * member. Only the Beheer pages and their APIs are restricted.
 */
export const ADMIN_ONLY_PREFIXES: readonly string[] = [
  '/admin/gebruikers',
  '/admin/logboek',
  '/admin/instellingen',
  '/admin/betalingen',
  '/admin/kassa',
  '/api/admin/users',
  '/api/admin/audit-log',
  '/api/admin/settings',
]

/** True when the path belongs to the administrator-only Beheer section. */
export function isAdminOnlyPath(pathname: string): boolean {
  return ADMIN_ONLY_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}?`),
  )
}

/** True when `role` may open `pathname` inside the back office. */
export function canAccessAdminPath(role: string | null | undefined, pathname: string): boolean {
  if (!isStaff(role)) return false
  if (isAdminOnlyPath(pathname)) return isAdminRole(role)
  return true
}

/** Where a signed-in user should land after logging in. */
export function landingPathForRole(role?: string | null): string {
  return isStaff(role) ? '/admin' : '/account'
}
